import React, { useState, useEffect, useMemo } from 'react';
import BornFreeStem1 from '../assets/BornFreeStem-1.webp';
import BornFreeStem2 from '../assets/BornFreeStem-2.webp';
import BornFreeStem3 from '../assets/BornFreeStem-3.webp';
import BornFreeStem4 from '../assets/BornFreeStem-4.webp';
import BornFreeStem5 from '../assets/BornFreeStem-5.webp';
import BornFreeStem6 from '../assets/BornFreeStem-6.webp';
import BornFreeStem7 from '../assets/BornFreeStem-7.webp';
import BornFreeStem8 from '../assets/BornFreeStem-8.webp';
import BornFreeStem9 from '../assets/BornFreeStem-9.webp';
import BornFreeStem10 from '../assets/BornFreeStem-10.webp';
import BornFreeStem11 from '../assets/BornFreeStem-11.webp';
import BornFreeStem12 from '../assets/BornFreeStem-12.webp';
import BornFreeStem13 from '../assets/BornFreeStem-13.webp';
import BornFreeStem14 from '../assets/BornFreeStem-14.webp';

interface StemCellAnimationProps {
  size?: number; // Size in pixels (default 120)
  speed?: number; // Animation speed in ms (default 400)
  static?: boolean; // If true, show only first frame (no animation)
  animateOnce?: boolean; // If true, animate once then stop
  onAnimationComplete?: () => void; // Callback when animation completes (only for animateOnce)
}

const stemFrames = [
  BornFreeStem1, BornFreeStem2, BornFreeStem3, BornFreeStem4, BornFreeStem5,
  BornFreeStem6, BornFreeStem7, BornFreeStem8, BornFreeStem9, BornFreeStem10,
  BornFreeStem11, BornFreeStem12, BornFreeStem13, BornFreeStem14
];

const StemCellAnimation: React.FC<StemCellAnimationProps> = ({ 
  size = 120,
  speed = 400,
  static: isStatic = false,
  animateOnce = false,
  onAnimationComplete
}) => {
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isAnimating, setIsAnimating] = useState(!isStatic && !animateOnce);

  // Watch for animation completion
  useEffect(() => {
    if (animateOnce && currentFrame === stemFrames.length - 1 && isAnimating) {
      // Reached last frame, wait a bit then stop and call callback
      const timeout = setTimeout(() => {
        setIsAnimating(false);
        if (onAnimationComplete) {
          onAnimationComplete();
        }
      }, speed);
      return () => clearTimeout(timeout);
    }
  }, [currentFrame, animateOnce, isAnimating, onAnimationComplete, speed]);

  // Start animation when animateOnce prop changes from false to true
  useEffect(() => {
    if (animateOnce && !isStatic && !isAnimating) {
      setIsAnimating(true);
      setCurrentFrame(0);
    }
  }, [animateOnce, isStatic, isAnimating]);

  useEffect(() => {
    if (isStatic) {
      setCurrentFrame(0);
      setIsAnimating(false);
      return;
    }

    if (!isAnimating) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentFrame(prev => {
        if (animateOnce && prev >= stemFrames.length - 1) {
          // Don't advance past last frame when animating once
          return prev;
        }
        return (prev + 1) % stemFrames.length;
      });
    }, speed);

    return () => clearInterval(interval);
  }, [speed, isStatic, animateOnce, isAnimating]);

  return (
    <div 
      className="relative" 
      style={{ 
        width: `${size}px`, 
        height: `${size}px`,
        flexShrink: 0
      }}
    >
      {/* Circular mask container */}
      <div className="absolute inset-0 rounded-full overflow-hidden">
        <img 
          src={stemFrames[currentFrame]}
          alt={`Stem cell animation frame ${currentFrame + 1}`}
          className="w-full h-full object-cover transition-all duration-200"
          style={{ 
            transform: `rotate(${currentFrame * 25.7}deg) scale(${0.9 + (currentFrame % 3) * 0.05})`,
          }}
          onError={(e) => {
            console.error(`Failed to load stem animation frame ${currentFrame + 1}`);
          }}
        />
      </div>
      
      {/* Outer glow effect */}
      <div 
        className="absolute inset-0 rounded-full opacity-30 pointer-events-none"
        style={{
          boxShadow: '0 0 15px rgba(16, 185, 129, 0.5), 0 0 30px rgba(255, 255, 255, 0.2)',
          transform: `scale(${1.1 + Math.sin(currentFrame * 0.3) * 0.05})`,
        }}
      />
    </div>
  );
};

export default StemCellAnimation;
